import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCustomer } from '../contexts/CustomerContext';
import LoadingSpinner from './Common/LoadingSpinner';
import PortalAccountNotice from './Common/PortalAccountNotice';

/**
 * ProtectedRoute — gates on having a usable PORTAL CUSTOMER, not merely on being signed in.
 *
 * This used to check `isAuthenticated` alone. Authentication and portal identity are not the
 * same thing: a contractor account (or any auth user with no `portal_users` row) passed this
 * gate and entered the portal. Only pages/Dashboard.js carried its own `customerNotFound`
 * check, so the dashboard explained itself while the other 20 routes rendered a blank shell
 * forever — they all load via `if (customer?.customer_id) { …fetch… }`, which is false when
 * `customer` is null, so no request is made and no error is raised. Reported live 2026-08-24
 * by signing in with a contractor email: 0 `portal_users` rows for it, so the message was
 * correct and the containment was not.
 *
 * Order matters. `loadError` is checked BEFORE `customerNotFound`: a failed lookup must not
 * be reported as "no account", because the account may exist and only the read failed.
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, authResolved, customerNotFound, loadError } = useCustomer();

  if (!authResolved) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loadError) {
    return <PortalAccountNotice variant="loadError" message={loadError} />;
  }

  if (customerNotFound) {
    return <PortalAccountNotice variant="notFound" />;
  }

  return children;
};

export default ProtectedRoute;
